import { AwsUi } from '../../../../core/runtime/src';
import { KeyCode } from '../../../../core/utils/src/dom';
import LegacyClassBuilder from '../../../../core/utils/src/class-builders/legacy';
import { Autosuggest } from '../../../input/src';
import { Button, ButtonDropdown } from '../../../button/src';
import { TokenGroup } from '../../../token/src';
import { ComponentFeature, FilteringOperation } from '../internal';
import { getCollectionComponent, renderSafely, ChangeHandler } from '../utility';
import filteringUtility from './utils';
import DropdownHandler from './dropdown-handler';
import TokenHandler from './token-handler';
var __updateController = function (oldValue, newValue) {
    if (oldValue !== newValue && this.__controller) {
        this.__controller.processComponentChange(ComponentFeature.PROPERTY_FILTERING, this.__getData(), false);
    }
};
var __handleChange = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    this.__changeHandler.apply(this, args);
};
var _a = AwsUi.__addComponents(['awsui-table-property-filtering', 'awsui-cards-property-filtering'], {
    releaseStatus: 'stable',
    generics: {
        Item: {
            defaultType: 'Table.Item'
        }
    },
    events: {
        // Fired when a value is selected from the property value group dropdown options.
        // The event `detail` contains an array of the current filter tokens, as well
        // as the operation applicable for filtering.
        propertyFilteringChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePropertyFiltering.ChangeDetail'
        },
        // Fired when a property is selected from the property group dropdown options.
        // The event `detail` contains the key of the currently selected property, as well
        // as the current filtering text.
        filteringInputPropertyChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePropertyFiltering.InputChangeDetail'
        },
        // Mimics the native `input` event on inputs.
        // The event `detail` contains the current value of the filtering input element.
        //
        // Use this event if you would like to update the list of filtering options yourself. Simply use the `value`
        // from the event detail to generate a new list of `filteringOptions`.
        //
        // If you are retrieving new filtering options from an asynchronous API, we strongly recommend to use the `filteringDelayedInput`
        // event to trigger the API request, and only use the `filteringInput` event to set the loading state.
        filteringInput: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePropertyFiltering.InputChangeDetail'
        },
        // Fired after the user has changed the value of the input field and stopped typing for a certain
        // period of time.
        // If you do not want to use a fixed set of filtering options, use this event to generate new filtering options
        // based on the user's input, especially when you are requesting the options from an asynchronous API.
        filteringDelayedInput: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePropertyFiltering.InputChangeDetail'
        }
    },
    regions: {
        // Displayed when there are no options to display.
        // This will not be shown when `filteringStatusType` is set to something that is not `null`.
        filteringEmpty: {}
    },
    properties: {
        // If set to `true`, the filtering input will be disabled.
        // Use it, for example, if you are fetching new items upon filtering change
        // in order to prevent the user from changing the filtering text.
        disabled: {
            type: 'boolean',
            defaultValue: false
        },
        // By default the component will filter the provided `filteringOptions` based on the value of the input field.
        // Only options that have a `value` or `label` which contains the input value as a substring
        // will be displayed in the list of options.
        //
        // If you set this property to `true` this default filtering mechanism will be disabled, hence all provided `filteringOptions` will be
        // displayed in the list of options. In that case make sure that you listen to the `filteringInput` or `filteringDelayedInput` event in order
        // to set the `filteringOptions` property to the options that are relevant for the user, given the input value.
        //
        // It should also be noted that disabling the default filtering will not disable match highlighting.
        disableDefaultFiltering: {
            type: 'boolean',
            defaultValue: false
        },
        // Label that will be passed down to the Autosuggest label property.
        // See the [Autosuggest API](/system/components/awsui-autosuggest/?tabId=api) page for more details.
        filteringLabel: {
            type: 'string',
            optional: true
        },
        // Text displayed in the filtering input element.
        filteringText: {
            type: 'string',
            optional: true,
            onChange: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var oldVal = args[0], newVal = args[1];
                if (!newVal) {
                    this.__currentProperty = null;
                    this.__currentPropertyPrefix = null;
                    this.__highlightText = null;
                }
                this.__changeHandler.apply(this, args);
            }
        },
        // Function that accepts the following parameters:
        // * `item`: the original item
        // * `tokens`: list of filtering tokens
        // * `operation`: string representing the operation to apply during filtering. Accepted values `['or', 'and']`
        //
        // The default value is a function that loops through the `filteringTokens`, and performs a case insensitive string match on
        // the value in the item for the `propertyKey` specified by the token and then will apply the given boolean operation `['or', 'and']`
        // for each match.
        //
        // If you explicitly set this property to `null` no actual filtering is performed on the list
        // of items. Use this option if you want to handle filtering yourself, for example if you want
        // to implement server-side filtering.
        //
        // In case of server-side filtering the `noMatch` region that you can set on Table/Cards component will never
        // be displayed. Use the `empty` region instead.
        filteringFunction: {
            type: 'function',
            functionType: 'TablePropertyFiltering.FilteringFunction',
            functionGenericType: 'Item',
            defaultValue: filteringUtility.defaultFilteringFunction,
            onChange: __handleChange,
            nullable: true
        },
        // `(resultsCount: integer): string`
        //
        // Given the number of results for the current filtering string, return a
        // human-readable, localized string displaying the number of results. For
        // example, "1 match" or "165 matches."
        //
        // If you set `filteringFunction` to `null` (for example, to implement
        // server-side filtering), this function receives `null` as an argument.
        // However, you should still provide the number of results. If the
        // total number of results is unknown, also include an indication that there
        // may be more results than the number listed. For example, "25+ matches."
        filteringCountTextFunction: {
            type: 'function',
            functionType: 'TablePropertyFiltering.FilteringCountTextFunction'
        },
        // Specifies the current status of loading more options.
        //
        // Setting it to `"loading"` will display a loading indicator next to the `filteringStatusText` in the dropdown footer,
        // while `"error"` will display an error indicator.
        filteringStatusType: {
            type: 'string',
            nullable: true,
            valid: ['error', 'loading'],
            onChange: __handleChange
        },
        // Text to be displayed in the dropdown footer when `filteringStatusType` is set.
        // Use this to indicate that more options are being loaded, or that an error occurred while loading.
        filteringStatusText: {
            type: 'string',
            onChange: __handleChange
        },
        // An array of options that will be displayed to the user in the form of a dropdown. This list should provide
        // the user with suggested values based on the user's input. Each option has the following properties:
        //
        // * `groupValuesLabel` [string]: Localized string to display for the 'Values' group label for a specific property. *For example: `EC2 instance values`*.
        // * `propertyKey` [string]: The object attribute name that corresponds to this property.
        // * `propertyLabel` [string]: A human-readable string for the property.
        // * `values` [Array<string>]: A list of all searchable string values for the property that will be used in the dropdown component.
        // * `group` [string]: Optional identifier of a custom group that this filtering option is assigned to. Use to create additional groups below the default one. Make sure to also define labels for the group in the `customGroupsText` property.
        //
        // Notice that only one level of options nesting is supported.
        //
        // If you would like to use the built-in filtering capabilities of this component, simply provide
        // a list of all valid options here and they will be automatically filtered based on the input
        // of the user.
        // Alternatively, you can listen to the `filteringInput` or `filteringDelayedInput` event and set new options
        // on your own.
        filteringOptions: {
            type: 'array',
            itemTypes: ['TablePropertyFiltering.Option'],
            onChange: __handleChange
        },
        // Placeholder text rendered when `filteringText` is an empty string.
        placeholder: {
            type: 'string'
        },
        // An array of tokens that will be displayed to the user beneath the filtering input. These tokens represent the
        // currently selected values to be used during filtering.
        // This list will be used by the `filteringFunction` along with the `operation` to filter down the items in the underlying dataset.
        // Each token has the following properties:
        //
        // * `propertyKey` [string]: The object attribute name that corresponds to this property.
        // * `propertyLabel` [string]: A human-readable string for the property.
        // * `value` [string]: The value selected from the filtering dropdown or free text submitted from the input field. Use the `isFreeText` property to detect the origin of the value. This value will be used by the `filteringFunction` in filtering of the underlying dataset.
        // * `label` [string]: A human-readable string for the value. E.g. When the value is '' but it would be preferred to display '-'.
        // * `negated` [boolean]: A boolean value used by the `filteringFunction` in filtering of the underlying dataset to specify if this token refers to a negation of a property. *For example: `and not`*.
        // * `isFreeText` [boolean]: A boolean value that indicates that this token represents free text submitted from the input field. It is used by the `filteringFunction` in filtering of the underlying dataset.
        //
        // It should be noted that the first token should never be negated. The internal filtering function of the component will
        // therefore ignore this value only for the first token.
        tokens: {
            type: 'array',
            itemTypes: ['TablePropertyFiltering.FilteringToken'],
            onChange: __handleChange
        },
        // A function to set the aria-label for the token deselect button.
        // The function accepts one argument that describes the token that the corresponding button removes.
        // The token has the following properties:
        //
        // * `propertyKey` [string]: The object attribute name that corresponds to this property.
        // * `propertyLabel` [string]: A human-readable string for the property.
        // * `value` [string]: The value selected from the filtering dropdown.
        // * `negated` [boolean]: A boolean value that describes whether the token is negated.
        removeTokenButtonLabel: {
            type: 'function',
            functionType: 'TablePropertyFiltering.RemoveTokenButtonLabelFunction'
        },
        // Filtering operation to be applied between tokens when filtering the items.
        // Valid operations include [`and`, `or`]
        operation: {
            type: 'string',
            defaultValue: FilteringOperation.AND,
            valid: ['and', 'or'],
            onChange: __handleChange
        },
        // If `hideOperations` it set, the indicator of the operation
        // and the selection of operations (applied to the property and value token) are hidden from the user.
        // Only use when you have a custom `filteringFunction` which combines tokens in different way than the default one.
        // When used, ensure that operators are communicated to the user in another way.
        hideOperations: {
            type: 'boolean',
            defaultValue: false
        },
        // A human-readable, localized string to display in the filtering dropdown for the `Properties` group label.
        groupPropertiesText: {
            type: 'string',
            onChange: __handleChange
        },
        // A human-readable, localized string to display in the filtering dropdown for the `Values` group label.
        groupValuesText: {
            type: 'string',
            onChange: __handleChange
        },
        // An array of objects that contain localized, human-readable strings for the labels of custom groups within the filtering dropdown.
        // Use `group` property to associate the strings with your custom group of options.
        // Define the following values for each group:
        //
        // * `properties` [string]: The group label in the filtering dropdown that contains the list of properties from this group. *For example: Tags*.
        // * `values` [string]: The group label in the filtering dropdown that contains the list of values from this group. *For example: Tags values*.
        // * `group` [string]: The identifier of a custom group.
        customGroupsText: {
            type: 'array',
            itemTypes: ['TablePropertyFiltering.GroupText']
        },
        // A human-readable, localized string to display in the token button dropdown for the `and` option.
        operationAndText: {
            type: 'string',
            onChange: __handleChange
        },
        // A human-readable, localized string to display in the token button dropdown for the `and not` option.
        operationNotAndText: {
            type: 'string',
            onChange: __handleChange
        },
        // A human-readable, localized string to display in the token button dropdown for the `or` option.
        operationOrText: {
            type: 'string',
            onChange: __handleChange
        },
        // A human-readable, localized string to display in the token button dropdown for the `or not` option.
        operationNotOrText: {
            type: 'string',
            onChange: __handleChange
        },
        // A human-readable, localized string to display in the `Clear filters` button used to clear all tokens.
        clearFiltersText: {
            type: 'string',
            onChange: __handleChange
        },
        // If set to `true`, the input will allow free text filtering tokens to be submitted.
        allowFreeTextFiltering: {
            type: 'boolean',
            defaultValue: false
        },
        __currentProperty: {
            type: 'object'
        },
        __currentPropertyPrefix: {
            type: 'string'
        },
        __highlightText: {
            type: 'string'
        }
    },
    callbacks: {
        initialized: function () {
            var _this = this;
            this.__changeHandler = new ChangeHandler(__updateController);
            this.__dropdownHandler = new DropdownHandler();
            this.__tokenHandler = new TokenHandler(this);
            this.node.addEventListener('awsui:__selectOption', function (event) { return _this.__selectOptionHandler(event); });
            this.__tokenSelectHandler = this.__tokenSelectHandler.bind(this);
            this.__keydownHandler = this.__keydownHandler.bind(this);
            this.__delayedInputHandler = this.__delayedInputHandler.bind(this);
            this.__filteringFocusHandler = this.__filteringFocusHandler.bind(this);
            this.__inputHandler = this.__inputHandler.bind(this);
            this.__openDropdown = function () {
                this.component.__openDropdown();
            };
        },
        attached: function () {
            this.__parentComponent = getCollectionComponent(this.node);
            this.__parentComponent.__propertyFiltering = this;
            this.__controller = this.__parentComponent.__controller;
            var autosuggest = this.node.querySelector('awsui-autosuggest');
            if (autosuggest) {
                autosuggest.addEventListener('click', this.__openDropdown);
            }
        },
        detached: function () {
            if (this.__parentComponent) {
                this.__parentComponent.__propertyFiltering = null;
                this.__controller = null;
            }
            var autosuggest = this.node.querySelector('awsui-autosuggest');
            if (autosuggest) {
                autosuggest.removeEventListener('click', this.__openDropdown);
            }
        }
    },
    functions: {
        __getData: function () {
            return {
                filteringText: this.filteringText,
                filteringFunction: this.filteringFunction,
                operation: this.operation,
                tokens: this.tokens,
                loading: this.__parentComponent.loading
            };
        },
        __getInputEventData: function () {
            var propertyKey = this.__currentProperty ? this.__currentProperty.propertyKey : null;
            return {
                value: this.filteringText,
                propertyKey: propertyKey
            };
        },
        __updateData: function (propertyFilteringData, fireEvent) {
            var _this = this;
            this.__changeHandler.prevent(function () {
                _this.filteringFunction = propertyFilteringData.filteringFunction;
                _this.operation = propertyFilteringData.operation;
                _this.tokens = propertyFilteringData.tokens;
                _this.loading = propertyFilteringData.loading;
            });
            if (fireEvent) {
                var eventDetail = {
                    operation: this.operation,
                    tokens: this.tokens
                };
                this.__fireEvent('propertyFilteringChange', eventDetail);
            }
        },
        __clearAllFilters: function () {
            var newData = this.__getData();
            newData.tokens = [];
            newData.operation = FilteringOperation.AND;
            this.__controller.processComponentChange(ComponentFeature.PROPERTY_FILTERING, newData, true);
        },
        __inputHandler: function (event) {
            event.stopPropagation();
            this.filteringText = event.target.component.value;
            if (this.__currentProperty) {
                // Clear Autosuggest text and current property if user backspaces after a property has been selected
                if (!this.filteringText ||
                    !(this.filteringText.indexOf(filteringUtility.getPropertyPrefixFormat(this.__currentPropertyPrefix)) === 0)) {
                    this.filteringText = null;
                    this.__currentProperty = null;
                    this.__currentPropertyPrefix = null;
                    this.__highlightText = null;
                }
                else {
                    // Highlight only the value after the property prefix
                    this.__highlightText = this.filteringText.substring(filteringUtility.getPropertyPrefixFormat(this.__currentPropertyPrefix).length);
                }
            }
            else {
                this.__highlightText = this.filteringText;
            }
            this.__fireEvent('filteringInput', this.__getInputEventData());
        },
        __delayedInputHandler: function (event) {
            event.stopPropagation();
            this.__fireEvent('filteringDelayedInput', this.__getInputEventData());
        },
        __filteringFocusHandler: function (event) {
            event.target.component.__openDropdown();
        },
        __selectOptionHandler: function (event) {
            event.stopPropagation();
            var selectedOption = event.detail.selectedOption;
            if (!selectedOption) {
                return;
            }
            if (selectedOption.group === 'property') {
                event.preventDefault();
                this.__currentPropertyPrefix = selectedOption.label;
                this.__currentProperty = selectedOption;
                this.__highlightText = null;
                this.filteringText = filteringUtility.getPropertyPrefixFormat(this.__currentPropertyPrefix);
                this.__fireEvent('filteringInputPropertyChange', this.__getInputEventData());
            }
        },
        __clearInputState: function () {
            this.__currentPropertyPrefix = undefined;
            this.__currentProperty = undefined;
            this.__highlightText = undefined;
            this.filteringText = undefined;
        },
        __tokenSelectHandler: function (event) {
            event.stopPropagation();
            var selectedOption = event.detail.selectedOption;
            if (!selectedOption) {
                return;
            }
            if (selectedOption.group === 'value') {
                this.__clearInputState();
                this.__tokenHandler.addPropertyToken(selectedOption);
            }
        },
        __keydownHandler: function (event) {
            if (event.detail.keyCode !== KeyCode.enter) {
                return;
            }
            event.preventDefault();
            if (!this.allowFreeTextFiltering) {
                return;
            }
            var value = this.filteringText;
            if (this.__currentPropertyPrefix) {
                value = value.slice(filteringUtility.getPropertyPrefixFormat(this.__currentPropertyPrefix).length);
            }
            if (value) {
                var currentProperty = this.__currentProperty;
                this.__clearInputState();
                this.__tokenHandler.addFreeTextToken(value, currentProperty);
            }
        },
        __generateOptions: function () {
            var customGroupsText = this.customGroupsText || [];
            var filteringText = this.filteringText || '';
            return this.__dropdownHandler.generateOptions(this.filteringOptions, filteringText, this.tokens, this.__currentProperty, this.groupPropertiesText, this.groupValuesText, customGroupsText, this.disableDefaultFiltering);
        },
        __renderTokens: function (h) {
            var _this = this;
            var dropdownItems = [
                { id: 'and', operation: FilteringOperation.AND, text: this.operationAndText, negated: false },
                { id: 'or', operation: FilteringOperation.OR, text: this.operationOrText, negated: false },
                { id: 'andNot', operation: FilteringOperation.AND, text: this.operationNotAndText, negated: true },
                { id: 'orNot', operation: FilteringOperation.OR, text: this.operationNotOrText, negated: true }
            ];
            var filteredDropdownItems = dropdownItems.filter(function (dropdownItem) { return dropdownItem.operation === _this.operation; });
            var dropdownItemsMap = dropdownItems.reduce(function (acc, item) {
                acc[item.id] = item;
                return acc;
            }, {});
            var renderOperationsDropdown = function (token, index) {
                if (index <= 0 || _this.hideOperations) {
                    return null;
                }
                // Only the 1st token contains all options. All subsequent tokens contain options based on selected operation.
                var items = index > 1 ? filteredDropdownItems : dropdownItems;
                var onItemClickHandler = function (event) {
                    event.stopPropagation();
                    _this.__tokenHandler.updateFilterToken(event.detail.id, token, items);
                };
                var selectedDropdownItem = dropdownItemsMap["" + _this.operation + (token.negated ? 'Not' : '')];
                return AwsUi.__createElement(ButtonDropdown, { text: selectedDropdownItem.text, items: items, itemClick: onItemClickHandler });
            };
            var renderToken = function (token, index) {
                var item = {
                    __labelPrefix: token.propertyLabel ? filteringUtility.getPropertyPrefixFormat(token.propertyLabel) : '',
                    label: token.label,
                    value: token.value,
                    disabled: _this.disabled,
                    dismissLabel: _this.removeTokenButtonLabel ? _this.removeTokenButtonLabel(token) : null
                };
                return AwsUi.__createElement(TokenGroup, { items: [item], dismiss: function () { return _this.__tokenHandler.removeFilterToken(index); } });
            };
            var cbToken = new LegacyClassBuilder(this).addComponentClass('token');
            return this.tokens.map(function (token, index) { return (AwsUi.__createElement("span", { className: cbToken.toClassName() },
                renderOperationsDropdown(token, index),
                renderToken(token, index))); });
        },
        __renderClearFiltersButton: function (h) {
            var _this = this;
            if (!this.tokens.length) {
                return null;
            }
            var cbClearFilters = new LegacyClassBuilder(this).addComponentClass('clear-filters');
            return (AwsUi.__createElement(Button, { className: cbClearFilters.toClassName(), formAction: "none", text: this.clearFiltersText, click: function () { return _this.__clearAllFilters(); } }));
        },
        __resultCount: function () {
            var items = this.__controller.getItems(ComponentFeature.SORTING);
            return items.length;
        },
        __shouldShowResults: function () {
            if (!this.__controller ||
                !this.tokens.length ||
                this.__parentComponent.loading ||
                !this.filteringCountTextFunction) {
                return false;
            }
            return true;
        },
        __getResultText: function () {
            var argument = this.__resultCount();
            if (!this.filteringFunction) {
                argument = null;
            }
            return this.filteringCountTextFunction(argument);
        }
    },
    render: renderSafely(function (m, h) {
        var cbTokens = new LegacyClassBuilder(this).addComponentClass('tokens-container');
        var cb = new LegacyClassBuilder(this).addComponentClass('container').addComponentClass('full-width');
        var filteringResultsClass = 'awsui-filtering-results' + (this.__shouldShowResults() ? '' : ' awsui-filtering-results__empty');
        return (AwsUi.__createElement("span", null,
            AwsUi.__createElement("span", { className: cb.toClassName() },
                AwsUi.__createElement(Autosuggest, { __highlightText: this.__highlightText, ariaDescribedby: null, disabled: this.disabled, filteringType: "manual", empty: this.filteringEmpty, label: this.filteringLabel, options: this.__generateOptions(), placeholder: this.placeholder, statusType: this.filteringStatusType, loadingText: this.filteringStatusType === 'loading' ? this.filteringStatusText : null, errorText: this.filteringStatusType === 'error' ? this.filteringStatusText : null, type: "search", value: this.filteringText, change: this.__tokenSelectHandler, delayedInput: this.__delayedInputHandler, focus: this.__filteringFocusHandler, input: this.__inputHandler, keydown: this.__keydownHandler }),
                AwsUi.__createElement("span", { "aria-live": "polite", "aria-atomic": "true", className: filteringResultsClass }, this.__shouldShowResults() ? this.__getResultText() : '')),
            AwsUi.__createElement("span", { className: cbTokens.toClassName() },
                this.__renderTokens(h),
                this.__renderClearFiltersButton(h))));
    })
}), TablePropertyFiltering = _a[0], CardsPropertyFiltering = _a[1];
export { TablePropertyFiltering, CardsPropertyFiltering };
//# sourceMappingURL=index.js.map